import type {
  AdminBookingDetailResponse,
  AdminBookingListResponse,
  AdminFinanceDetailResponse,
  AdminFinanceListResponse,
  AdminOverviewResponse,
  AdminVenueDetailResponse,
  AdminVenueListResponse,
} from "@/types/services/api-contracts";

import { callEdgeData } from "./_core/edge-call";
import { ADMIN_FUNCTION_NAME } from "./_core/function-names";

export const adminService = {
  async getOverview(): Promise<AdminOverviewResponse> {
    return callEdgeData<AdminOverviewResponse>(ADMIN_FUNCTION_NAME, "get-overview", {
      method: "GET",
    });
  },

  async listVenues(filters: {
    query?: string;
    status?: string;
    state?: string;
    city?: string;
    missing_active_courts?: string;
    missing_coordinates?: string;
    subscription_status?: string;
  } = {}): Promise<AdminVenueListResponse> {
    return callEdgeData<AdminVenueListResponse>(ADMIN_FUNCTION_NAME, "list-venues", {
      method: "GET",
      params: Object.fromEntries(Object.entries(filters).filter(([, value]) => typeof value === "string" && value.length > 0)),
    });
  },

  async listVenueIndications(filters: {
    query?: string;
    city?: string;
    lead_status?: string;
    campaign_tag?: string;
    is_published?: string;
  } = {}): Promise<{
    items: Array<Record<string, unknown>>;
    total: number;
    limit: number;
    contains_sensitive_contact_data: boolean;
  }> {
    return callEdgeData<{
      items: Array<Record<string, unknown>>;
      total: number;
      limit: number;
      contains_sensitive_contact_data: boolean;
    }>(ADMIN_FUNCTION_NAME, "list-venue-indications", {
      method: "GET",
      params: Object.fromEntries(Object.entries(filters).filter(([, value]) => typeof value === "string" && value.length > 0)),
    });
  },

  async getVenueDetail(venueId: string): Promise<AdminVenueDetailResponse> {
    return callEdgeData<AdminVenueDetailResponse>(ADMIN_FUNCTION_NAME, "get-venue-detail", {
      method: "GET",
      params: { venue_id: venueId },
    });
  },

  async publishVenue(input: { venue_id: string; reason: string }): Promise<{
    id: string;
    tenant_id: string;
    name: string;
    status: string;
    updated_at: string;
  }> {
    return callEdgeData<{ id: string; tenant_id: string; name: string; status: string; updated_at: string }>(
      ADMIN_FUNCTION_NAME,
      "publish-venue",
      { body: input },
    );
  },

  async hideVenue(input: { venue_id: string; reason: string }): Promise<{
    id: string;
    tenant_id: string;
    name: string;
    status: string;
    updated_at: string;
  }> {
    return callEdgeData<{ id: string; tenant_id: string; name: string; status: string; updated_at: string }>(
      ADMIN_FUNCTION_NAME,
      "hide-venue",
      { body: input },
    );
  },

  async listBookings(filters: {
    query?: string;
    venue_id?: string;
    status?: string;
    payment_status?: string;
    date_from?: string;
    date_to?: string;
  } = {}): Promise<AdminBookingListResponse> {
    return callEdgeData<AdminBookingListResponse>(ADMIN_FUNCTION_NAME, "list-bookings", {
      method: "GET",
      params: Object.fromEntries(Object.entries(filters).filter(([, value]) => typeof value === "string" && value.length > 0)),
    });
  },

  async getBookingDetail(bookingId: string): Promise<AdminBookingDetailResponse> {
    return callEdgeData<AdminBookingDetailResponse>(ADMIN_FUNCTION_NAME, "get-booking-detail", {
      method: "GET",
      params: { booking_id: bookingId },
    });
  },

  async cancelBookingAdmin(input: { booking_id: string; reason: string }): Promise<{
    success: boolean;
    delegated: Record<string, unknown>;
    booking: Record<string, unknown> | null;
  }> {
    return callEdgeData<{ success: boolean; delegated: Record<string, unknown>; booking: Record<string, unknown> | null }>(
      ADMIN_FUNCTION_NAME,
      "cancel-booking-admin",
      { body: input },
    );
  },

  async listFinance(filters: {
    query?: string;
    tenant_id?: string;
    payment_status?: string;
    provider?: string;
    date_from?: string;
    date_to?: string;
  } = {}): Promise<AdminFinanceListResponse> {
    return callEdgeData<AdminFinanceListResponse>(ADMIN_FUNCTION_NAME, "list-finance", {
      method: "GET",
      params: Object.fromEntries(Object.entries(filters).filter(([, value]) => typeof value === "string" && value.length > 0)),
    });
  },

  async getFinanceDetail(paymentId: string): Promise<AdminFinanceDetailResponse> {
    return callEdgeData<AdminFinanceDetailResponse>(ADMIN_FUNCTION_NAME, "get-finance-detail", {
      method: "GET",
      params: { payment_id: paymentId },
    });
  },

  async reconcilePaymentAdmin(input: { booking_id: string; reason: string }): Promise<{
    success: boolean;
    delegated: Record<string, unknown>;
    booking: Record<string, unknown> | null;
    payments: Record<string, unknown>[];
  }> {
    return callEdgeData<{ success: boolean; delegated: Record<string, unknown>; booking: Record<string, unknown> | null; payments: Record<string, unknown>[] }>(
      ADMIN_FUNCTION_NAME,
      "reconcile-payment-admin",
      { body: input },
    );
  },
};
